/**
 * combo.ts — click-combo multiplier. Consecutive clicks within a time window
 * stack a bonus to click power; the combo lapses once the window passes (checked
 * lazily so it costs nothing per tick and resolves correctly during offline
 * catch-up where nobody clicks). Deterministic: uses gameTimeMs, never Date.now().
 */
import type { GameState } from '../core/GameState';
import { BALANCE } from '../data';
import { safeNumber } from '../core/numbers';

/** Effective combo stack right now (0 once the window has lapsed). */
export function comboStacks(state: GameState): number {
  const { window } = BALANCE.combo;
  if (state.meta.gameTimeMs - state.combo.lastClickAt > window) return 0;
  return state.combo.count;
}

/** Click-power multiplier from the current combo (1 .. 1+maxBonus). */
export function comboMultiplier(state: GameState): number {
  const { step, maxBonus } = BALANCE.combo;
  const bonus = Math.min(maxBonus, comboStacks(state) * step);
  return safeNumber(1 + bonus, 1);
}

/** Register a click: extend the combo if within the window, else restart it. */
export function registerClick(state: GameState): void {
  const { window, step, maxBonus } = BALANCE.combo;
  const now = state.meta.gameTimeMs;
  const maxCount = Math.ceil(maxBonus / step);
  if (now - state.combo.lastClickAt <= window) {
    state.combo.count = Math.min(maxCount, state.combo.count + 1);
  } else {
    state.combo.count = 1;
  }
  state.combo.lastClickAt = now;
  if (state.combo.count > state.stats.maxCombo) state.stats.maxCombo = state.combo.count;
}
